import type { ShadowPlanDriver } from '../../services/shadowPlans.api';
import { formatTimeBaghdad } from '../../utils/date';
import { tbl } from '../ui/table';
import { cn } from '../../utils/cn';
import { KIND_LABEL, VAN_LABEL } from './shadowPlanView';

interface Props {
  driver: ShadowPlanDriver & { color: string };
}

/** One planned driver's stops in route order: when, where, what. */
export default function ShadowDriverStops({ driver }: Props) {
  const stops = [...driver.stops].sort((a, b) => a.seq - b.seq);
  const rto = stops.reduce((n, s) => n + s.rtoParcels, 0);
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-3" aria-label={`Driver ${driver.label} stops`}>
      <h3 className="flex flex-wrap items-center gap-x-2 text-sm font-medium text-gray-800">
        <span aria-hidden className="h-2.5 w-2.5 rounded-full" style={{ background: driver.color }} />
        Driver {driver.label}
        <span className="font-normal text-gray-500 tabular-nums">
          {VAN_LABEL[driver.vehicleType]} · {formatTimeBaghdad(driver.startAt)}–{formatTimeBaghdad(driver.endAt)} · {stops.length} stops ·{' '}
          {driver.orders} orders to pick up · {rto} returns
        </span>
      </h3>
      <div className={cn(tbl.wrap, 'mt-2')}>
        <table className={tbl.table}>
          <thead>
            <tr className={tbl.headRow}>
              <th className={tbl.th}>#</th>
              <th className={tbl.th}>Arrive</th>
              <th className={tbl.th}>Merchant</th>
              <th className={tbl.th}>Stop</th>
              <th className={cn(tbl.th, tbl.num)}>Orders</th>
              <th className={cn(tbl.th, tbl.num)}>Returns</th>
              <th className={tbl.th}>Allowed time</th>
              <th className={tbl.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {stops.map((s) => (
              <tr key={s.seq} className={tbl.row}>
                <td className={cn(tbl.td, tbl.muted)}>{s.seq}</td>
                <td className={cn(tbl.td, 'tabular-nums')}>{formatTimeBaghdad(s.eta)}</td>
                <td className={cn(tbl.td, 'text-left')}>
                  <span dir="auto">{s.merchantName ?? (s.merchantId != null ? `Merchant ${s.merchantId}` : '—')}</span>
                </td>
                <td className={tbl.td}>
                  {KIND_LABEL[s.kind]}
                  {s.isCold && <span className="ml-1.5 rounded bg-cyan-100 px-1.5 py-0.5 text-[11px] font-medium text-cyan-800">Cold</span>}
                </td>
                <td className={cn(tbl.td, tbl.num)}>{s.orders}</td>
                <td className={cn(tbl.td, tbl.num)}>{s.rtoParcels}</td>
                <td className={cn(tbl.td, tbl.muted)}>
                  {formatTimeBaghdad(s.windowStart)}–{formatTimeBaghdad(s.windowEnd)}
                </td>
                <td className={tbl.td}>{s.isForecast ? <span className="text-gray-500">Expected</span> : 'Booked'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
